import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import Popper from '@mui/material/Popper';

import { BASE_URL } from 'utils/baseUrlProvider';
import axiosClient from 'utils/axios';

const BLUR_DELAY_MS = 100;

const PatientSearchField = ({ 
  value, 
  onChange, 
  error, 
  helperText, 
  onPatientSelect,
  onSelectionComplete,
  field,
  placeholder = "Enter Name",
  allowCustomEntry = false,
  disabled = false
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState(value || '');
  const [searchResults, setSearchResults] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDropdownFocused, setIsDropdownFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const searchGenerationRef = useRef(0);
  const pendingInputValueRef = useRef(null);

  const handleSearch = useMemo(
    () =>
      debounce(
        async (query, generation) => {
          if (!query || query.trim().length === 0) {
            if (generation === searchGenerationRef.current) {
              setSearchResults([]);
              setSelectedIndex(-1);
            }
            return;
          }

          try {
            let response;
            const trimmed = query.trim();

            if (trimmed.length <= 2) {
              response = await axiosClient.get(`${BASE_URL}/users/details/`, {
                params: { ordering: '-updated_at', limit: 30 }
              });
              const filteredResults = (response.data.results || response.data || []).filter((user) =>
                user.full_name?.toLowerCase().includes(trimmed.toLowerCase()) ||
                user.phone_number?.includes(trimmed) ||
                user.id?.toLowerCase().includes(trimmed.toLowerCase())
              );
              if (generation !== searchGenerationRef.current) return;
              setSearchResults(filteredResults.slice(0, 7));
            } else {
              response = await axiosClient.get(`${BASE_URL}/users/search/`, {
                params: { query: trimmed, limit: 10, offset: 0 }
              });
              const results =
                response.data?.results || (Array.isArray(response.data) ? response.data : []);
              if (generation !== searchGenerationRef.current) return;
              setSearchResults(results.slice(0, 7));
            }
          } catch (error) {
            if (generation !== searchGenerationRef.current) return;
            console.error('Error searching patients:', error);
            setSearchResults([]);
          }
          if (generation === searchGenerationRef.current) {
            setSelectedIndex(-1);
          }
        },
        250
      ),
    []
  );

  const closeDropdown = useCallback(() => {
    searchGenerationRef.current += 1;
    handleSearch.cancel();
    setSearchResults([]);
    setAnchorEl(null);
    setSelectedIndex(-1);
    setIsDropdownFocused(false);
  }, [handleSearch]);

  useEffect(() => {
    const nextValue = value || '';
    if (pendingInputValueRef.current === nextValue) {
      pendingInputValueRef.current = null;
      return;
    }

    setSearchQuery(nextValue);
    closeDropdown();
  }, [value, closeDropdown]);

  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, [handleSearch]);

  const handleInputChange = (event) => {
    const value = event.target.value;
    const generation = searchGenerationRef.current + 1;
    searchGenerationRef.current = generation;
    pendingInputValueRef.current = value;
    setSearchQuery(value);
    handleSearch(value, generation);
    onChange(value);
    setAnchorEl(event.currentTarget);
    setSelectedIndex(-1);
  };

  const handleInputFocus = (event) => {
    if (!anchorEl) {
      setAnchorEl(event.currentTarget);
    }
    setSelectedIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (searchResults.length === 0) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % searchResults.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev <= 0 ? searchResults.length - 1 : prev - 1));
    } else if (event.key === 'Enter') {
      if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
        event.preventDefault();
        const selectedResult = searchResults[selectedIndex];
        setSearchQuery(selectedResult.full_name);
        closeDropdown();
        onPatientSelect(selectedResult);
        if (onSelectionComplete) {
          onSelectionComplete(selectedResult);
        }
        setSelectedIndex(-1);
        setIsDropdownFocused(false);
      }
    } else if (event.key === 'Escape') {
      closeDropdown();
    }
  };


  const handleBlur = (event) => {
    const relatedTarget = event.relatedTarget || document.activeElement;
    if (dropdownRef.current && dropdownRef.current.contains(relatedTarget)) {
      setIsDropdownFocused(true);
      return;
    }
    
    setTimeout(() => {
      if (!isDropdownFocused) {
        const hasNoResults = searchResults.length === 0;
        closeDropdown();
        
        if (allowCustomEntry && searchQuery && hasNoResults) {
          const customEntry = {
            id: null,
            full_name: searchQuery,
            isCustom: true
          };
          onPatientSelect(customEntry);
        }
      }
    }, BLUR_DELAY_MS);
  };

  const handleDropdownBlur = () => {
    setIsDropdownFocused(false);
    setTimeout(() => {
      if (document.activeElement !== inputRef.current && 
          (!dropdownRef.current || !dropdownRef.current.contains(document.activeElement))) {
        closeDropdown();
      }
    }, BLUR_DELAY_MS);
  };

  useEffect(() => {
    if (selectedIndex >= 0) {
      const dropdownNode = dropdownRef.current;
      const option = dropdownNode?.querySelector(`[data-patient-option-index="${selectedIndex}"]`);
      const isVisible = dropdownNode && dropdownNode.offsetHeight > 0 && dropdownNode.offsetWidth > 0;
      if (option && isVisible) {
        option.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  return (
    <Stack spacing={1} sx={{ position: 'relative' }}>
      {field && <InputLabel htmlFor="patient-search">{field}</InputLabel>}
      <TextField
        fullWidth
        id="patient-search"
        placeholder={placeholder}
        value={searchQuery}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        error={error}
        helperText={helperText}
        disabled={disabled}
        autoComplete="off"
        inputRef={inputRef}
        inputProps={{
          'aria-autocomplete': 'list',
          'aria-controls': searchResults.length > 0 ? 'patient-search-results' : undefined,
          'aria-expanded': searchResults.length > 0,
          'aria-activedescendant': selectedIndex >= 0 ? `patient-option-${selectedIndex}` : undefined
        }}
      />
      <Popper 
        open={searchResults.length > 0} 
        anchorEl={anchorEl} 
        placement="bottom-start" 
        style={{ zIndex: 1300 }}
        data-patient-dropdown={searchResults.length > 0 ? 'true' : undefined}
        modifiers={[{ name: 'flip', enabled: false }, { name: 'preventOverflow', options: { altAxis: false, tether: false } }]}
      >
        <Box
          ref={dropdownRef}
          tabIndex={-1}
          onBlur={handleDropdownBlur}
          role="listbox"
          id="patient-search-results"
          aria-activedescendant={selectedIndex >= 0 ? `patient-option-${selectedIndex}` : undefined}
          sx={{
            background: 'white',
            boxShadow: 1,
            borderRadius: 1,
            border: `1px solid ${theme.palette.grey[300]}`,
            width: anchorEl ? anchorEl.clientWidth : '100%',
            maxHeight: 400,
            overflowY: 'auto'
          }}
          data-patient-dropdown-panel="true"
        >
          {searchResults.map((result, index) => (
            <Box
              key={result.id}
              id={`patient-option-${index}`}
              data-patient-option-index={index}
              role="option"
              aria-selected={selectedIndex === index}
              sx={{
                padding: 1,
                cursor: 'pointer',
                backgroundColor: selectedIndex === index ? theme.palette.action.hover : 'transparent',
                '&:hover': { background: theme.palette.action.hover },
              }}
              onClick={() => {
                setSearchQuery(result.full_name);
                closeDropdown();
                onPatientSelect(result);
                if (onSelectionComplete) {
                  onSelectionComplete(result);
                }
                setSelectedIndex(-1);
                setIsDropdownFocused(false);
              }}
            >
              <strong>{result.full_name}</strong>
              <Box>Id: {result.id}</Box>
              {(result?.careof_name || result?.metadata?.careof_name) && (
                <Box>
                  {result?.metadata?.careof_suffix ? `${result.metadata.careof_suffix} ` : ''}{result?.careof_name || result?.metadata?.careof_name} ({result?.careof || 'N/A'})
                </Box>
              )}
              {result?.phone_number && <Box>Phone: {result?.phone_number || 'N/A'}</Box>}
              {result?.aadhar_card && <Box>Aadhar: {result?.aadhar_card || 'N/A'}</Box>}
            </Box>
          ))}
        </Box>
      </Popper>
    </Stack>
  );
};

PatientSearchField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  onPatientSelect: PropTypes.func.isRequired,
  onSelectionComplete: PropTypes.func,
  field: PropTypes.string,
  placeholder: PropTypes.string,
  allowCustomEntry: PropTypes.bool
};

export default PatientSearchField;